import React, { useState } from "react";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { prepareFolderApi, PrepareFolderResponse, rebuildVoiceoversApi } from "api/fastApi/healthCheck";
import { MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { message, Tooltip } from "antd";
import { useIsEnglishUi } from "utils/uiLanguage";
import { AutoEditGate, useAutoEditBlock } from "../ManualOnly/AutoEditGate";

const DISMISS_MS = 5000;

const spinKeyframes = `
@keyframes prepSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

export const FolderPrepare: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [status, setStatus] = useState<"idle" | "running" | "done" | "error">("idle");
    const [progressText, setProgressText] = useState("");

    const fromEng = useIsEnglishUi();

    const labels = {
        button: fromEng ? "Prepare Voicing" : "Подготовить озвучку",
        hint: fromEng
            ? "Scan & auto-fix translation side: missing translations, missing audio, voice mismatches, timing drift"
            : "Сканировать и исправить перевод: отсутствие перевода, аудио, голоса, тайминги",
        running: fromEng ? "Preparing voicing..." : "Подготовка озвучки...",
        error: fromEng ? "Voicing prepare failed" : "Ошибка подготовки озвучки",
    };

    const handleClick = async () => {
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.error);
            return;
        }

        setStatus("running");
        setProgressText(labels.running);

        try {
            const res = await prepareFolderApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
            });

            if (res.error) {
                setStatus("error");
                setProgressText(res.error);
                return;
            }

            if (res.summary.total_fixed === 0 && res.summary.total_flagged === 0) {
                setStatus("done");
                setProgressText(fromEng ? "No issues found" : "Проблем не найдено");
                setTimeout(() => setStatus("idle"), DISMISS_MS);
                return;
            }

            setStatus("done");

            // Build summary text
            const parts: string[] = [];
            const tr = res.phases.translation || {};

            if (tr.t1_missing?.translated)
                parts.push(`${tr.t1_missing.translated} translated`);
            if (tr.t2_missing_audio?.synthesized)
                parts.push(`${tr.t2_missing_audio.synthesized} audio generated`);
            if (tr.t3_orphans?.deleted)
                parts.push(`${tr.t3_orphans.deleted} orphans removed`);
            if (tr.t5_voice_mismatch?.fixed)
                parts.push(`${tr.t5_voice_mismatch.fixed} voices fixed`);
            if (tr.t4_timing?.adjusted)
                parts.push(`${tr.t4_timing.adjusted} timing adjusted`);

            setProgressText(
                parts.length ? `Fixed: ${parts.join(", ")}` : (fromEng ? "Done" : "Готово")
            );

            // Refresh subtitles after fixes
            if (res.summary.total_fixed > 0) {
                await handles.refreshAllSubtitles();
            }

            setTimeout(() => setStatus("idle"), DISMISS_MS);
        } catch (error: any) {
            console.error("FolderPrepare error:", error);
            setStatus("error");
            setProgressText(error.message || "Unknown error");
            message.error(labels.error);
        }
    };

    const helpIconStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.1em",
        height: "1.1em",
        borderRadius: "50%",
        border: "1px solid #888",
        fontSize: "0.75em",
        lineHeight: 1,
        cursor: "help",
        userSelect: "none",
    };

    // Show banner when running/done/error
    if (status !== "idle") {
        const bgColor =
            status === "running" ? "#e6f4ff" : status === "done" ? "#f6ffed" : "#fff2f0";
        const borderColor =
            status === "running" ? "#91caff" : status === "done" ? "#b7eb8f" : "#ffccc7";
        const icon =
            status === "running" ? "hourglass_empty" : status === "done" ? "check_circle" : "error";

        return (
            <>
                <style>{spinKeyframes}</style>
                <div
                    style={{
                        padding: "6px 16px",
                        backgroundColor: bgColor,
                        borderBottom: `1px solid ${borderColor}`,
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <span
                        className="material-icons"
                        style={{
                            fontSize: 16,
                            color:
                                status === "done"
                                    ? "#52c41a"
                                    : status === "error"
                                      ? "#ff4d4f"
                                      : "#1677ff",
                            animation:
                                status === "running" ? "prepSpin 1.5s linear infinite" : undefined,
                        }}
                    >
                        {icon}
                    </span>
                    <span>{progressText}</span>
                    {status !== "running" && (
                        <span
                            style={{ cursor: "pointer", marginLeft: "auto", opacity: 0.6 }}
                            onClick={() => setStatus("idle")}
                        >
                            x
                        </span>
                    )}
                </div>
            </>
        );
    }

    // Default: show button
    return null;
};

/**
 * Standalone button for use in Footer (next to other controls).
 */
export const FolderPrepareButton: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [loading, setLoading] = useState(false);
    // Переводит недостающее, синтезирует, удаляет сирот, меняет голоса и тайминги — автоправка.
    const autoEdit = useAutoEditBlock();

    const fromEng = useIsEnglishUi();

    const labels = {
        button: fromEng ? "Scan & fix" : "Проверить и починить",
        hint: fromEng
            ? "Scan & auto-fix translation side: missing translations, missing audio, voice mismatches, timing drift"
            : "Сканировать и исправить перевод: отсутствие перевода, аудио, голоса, тайминги",
        success: fromEng ? "Voicing prepared, reloading..." : "Озвучка подготовлена, загрузка...",
        noIssues: fromEng ? "No issues found" : "Проблем не найдено",
        error: fromEng ? "Voicing prepare failed" : "Ошибка подготовки озвучки",
    };

    const handleClick = async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.error);
            return;
        }

        setLoading(true);
        try {
            const res = await prepareFolderApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
            });

            if (res.error) {
                message.error(res.error);
                return;
            }

            if (res.summary.total_fixed === 0 && res.summary.total_flagged === 0) {
                message.success(labels.noIssues);
                return;
            }

            message.success(`${labels.success} (${res.summary.total_fixed} fixed, ${res.summary.total_flagged} flagged)`);

            if (res.summary.total_fixed > 0) {
                await handles.refreshAllSubtitles();
            }
        } catch (error: any) {
            console.error("FolderPrepare error:", error);
            message.error(labels.error);
        } finally {
            setLoading(false);
        }
    };

    const helpIconStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.1em",
        height: "1.1em",
        borderRadius: "50%",
        border: "1px solid #888",
        fontSize: "0.75em",
        lineHeight: 1,
        cursor: "help",
        userSelect: "none",
    };

    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 6 }}>
                <AutoEditGate {...autoEdit}>
                    <ButtonH sm ghost onClick={handleClick} disabled={loading || autoEdit.blocked}>
                        <Ico
                            size={"16"}
                            style={
                                loading
                                    ? { animation: "prepSpin 1s linear infinite", display: "inline-block" }
                                    : undefined
                            }
                        >
                            build_circle
                        </Ico>
                        {labels.button}
                    </ButtonH>
                </AutoEditGate>
                <Tooltip title={labels.hint}>
                    <span style={helpIconStyle}>?</span>
                </Tooltip>
            </div>
        </>
    );
};

/**
 * Rebuild all voiceovers: calculate optimal speed, re-synth all, fix positions.
 */
export const RebuildVoiceoversButton: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [loading, setLoading] = useState(false);
    // Manual speed override. Blank = auto-compute (optimal speed from RU timing).
    const [speedStr, setSpeedStr] = useState<string>("");
    // Пересинтезирует всю папку и сбрасывает расставленные руками позиции — автоправка.
    const autoEdit = useAutoEditBlock();

    const fromEng = useIsEnglishUi();

    const labels = {
        button: fromEng ? "Rebuild" : "Пересоздать",
        hint: fromEng
            ? "Re-synthesize all voiceovers, reset positions, fix overlaps. Speed: blank = auto, or set e.g. 1.1"
            : "Пересинтезировать всю озвучку, сбросить позиции, исправить наложения. Скорость: пусто = авто, либо задайте, напр. 1.1",
        confirm: fromEng
            ? "This will re-synthesize ALL voiceovers in this folder. Continue?"
            : "Все озвучки в папке будут пересозданы. Продолжить?",
        success: fromEng ? "Voiceovers rebuilt!" : "Озвучка пересоздана!",
        error: fromEng ? "Rebuild failed" : "Ошибка пересоздания",
    };

    const handleClick = async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.error);
            return;
        }

        if (!window.confirm(labels.confirm)) return;

        // Parse optional manual speed override (blank = auto-compute on the backend).
        let speed: number | undefined;
        const trimmed = speedStr.trim();
        if (trimmed) {
            const parsed = parseFloat(trimmed.replace(",", "."));
            if (Number.isNaN(parsed) || parsed < 0.7 || parsed > 1.2) {
                message.error(fromEng ? "Speed must be between 0.7 and 1.2" : "Скорость должна быть от 0.7 до 1.2");
                return;
            }
            speed = parsed;
        }

        setLoading(true);
        try {
            // Persist any pending/in-flight edits to disk BEFORE the backend
            // re-synthesizes. rebuild_all_voiceovers reads captions straight from
            // the on-disk {id}_{lang}.json files, so without this flush it would
            // rebuild from stale text (debounced autosave not yet landed).
            try {
                await handles.flushGate();
            } catch (e) {
                console.error("[REBUILD] flushGate FAILED - aborting to avoid rebuilding stale text:", e);
                message.error(labels.error);
                return;
            }

            const res = await rebuildVoiceoversApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                speed,
            });

            if (res.error) {
                message.error(res.error);
                return;
            }

            message.success(`${labels.success} ${res.summary}`);
            await handles.refreshAllSubtitles();
        } catch (error: any) {
            console.error("Rebuild error:", error);
            message.error(labels.error);
        } finally {
            setLoading(false);
        }
    };

    const helpIconStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.1em",
        height: "1.1em",
        borderRadius: "50%",
        border: "1px solid #888",
        fontSize: "0.75em",
        lineHeight: 1,
        cursor: "help",
        userSelect: "none",
    };

    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 6 }}>
                <AutoEditGate {...autoEdit}>
                    <ButtonH sm ghost onClick={handleClick} disabled={loading || autoEdit.blocked}>
                        <Ico
                            size={"16"}
                            style={
                                loading
                                    ? { animation: "prepSpin 1s linear infinite", display: "inline-block" }
                                    : undefined
                            }
                        >
                            replay
                        </Ico>
                        {labels.button}
                    </ButtonH>
                </AutoEditGate>
                <input
                    type="number"
                    step="0.05"
                    min="0.7"
                    max="1.2"
                    value={speedStr}
                    onChange={(e) => setSpeedStr(e.target.value)}
                    disabled={loading}
                    placeholder={fromEng ? "auto" : "авто"}
                    title={fromEng ? "Speed (blank = auto, e.g. 1.1)" : "Скорость (пусто = авто, напр. 1.1)"}
                    style={{ width: 56, padding: "2px 4px", fontSize: "0.85em" }}
                />
                <Tooltip title={labels.hint}>
                    <span style={helpIconStyle}>?</span>
                </Tooltip>
            </div>
        </>
    );
};
